import React, { useEffect, useState } from "react";
import { Zap, Train } from "lucide-react";
import { NextDepartureSkeleton } from "./skeletons";

/**
 * NextDeparture — indigo-tinted countdown card for the right rail.
 *
 * Renders the next upcoming PNR's departure time as a 3-up
 * Days / Hours / Minutes countdown. Self-updates every 30 seconds.
 *
 * If the train has already departed and is within 12 hours of departure,
 * switches to "Departed · in transit now". Beyond that, the card hides
 * itself (returns null).
 *
 * Props:
 *   pnr — the next-up PNR, or null. Expects:
 *         { pnr_number, train_name, train_number,
 *           boarding_station, boarding_station_name,
 *           destination_station, destination_station_name,
 *           journey_date (YYYY-MM-DD), departure_time (HH:mm) }
 */

const computeDelta = (pnr) => {
  if (!pnr || !pnr.journey_date) return null;
  // Combine journey_date + departure_time. We treat the time as IST
  // (Asia/Kolkata) since that's what IRCTC reports — but we don't need a
  // full TZ library: take the local representation as a best effort.
  const time = pnr.departure_time || "00:00";
  const ts = Date.parse(`${pnr.journey_date}T${time}:00`);
  if (Number.isNaN(ts)) return null;
  return ts - Date.now();
};

const parts = (ms) => {
  const totalSecs = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSecs / 86400);
  const hours = Math.floor((totalSecs % 86400) / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  return { days, hours, mins };
};

const NextDeparture = ({ pnr, loading = false }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  if (loading) return <NextDepartureSkeleton />;

  if (!pnr) return null;

  const delta = computeDelta(pnr);
  if (delta == null) return null;

  // Suppress card once we're more than 12h past departure.
  if (delta < -12 * 3600 * 1000) return null;

  const departed = delta <= 0;
  const { days, hours, mins } = parts(delta);

  return (
    <div
      className="relative rounded-xl border border-indigo-500/20 bg-gradient-to-br from-indigo-500/[0.06] via-zinc-900/40 to-zinc-900/40 p-4 overflow-hidden"
      data-testid="next-departure-card"
    >
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />

      <div className="relative">
        {/* Overline */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-md bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center shrink-0">
            <Zap size={14} className="text-indigo-300" strokeWidth={2.2} />
          </div>
          <span className="text-[10.5px] tracking-[0.18em] uppercase text-zinc-500 font-semibold font-mono">
            Next Departure
          </span>
        </div>

        {/* Train + route */}
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <Train size={14} className="text-zinc-500 shrink-0" />
          <span className="text-[13.5px] font-semibold text-zinc-50 truncate font-heading">
            {pnr.train_name}
          </span>
          {pnr.train_number && (
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-zinc-800 border border-zinc-700 text-zinc-400 whitespace-nowrap font-mono">
              {pnr.train_number}
            </span>
          )}
        </div>
        <div className="text-[11.5px] text-zinc-400 mb-3 font-mono truncate">
          {pnr.boarding_station} → {pnr.destination_station} ·{" "}
          {pnr.departure_time}
        </div>

        {/* Countdown OR departed banner */}
        {departed ? (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-sky-500/10 border border-sky-500/30">
            <span className="relative inline-flex w-2 h-2 shrink-0">
              <span className="absolute inset-0 rounded-full bg-sky-400/60 animate-ping" />
              <span className="relative w-2 h-2 rounded-full bg-sky-400" />
            </span>
            <span className="text-[12px] text-sky-300 font-medium">
              Departed · in transit now
            </span>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: days, l: "Days" },
              { v: hours, l: "Hours" },
              { v: mins, l: "Min" },
            ].map((part) => (
              <div
                key={part.l}
                className="bg-zinc-950/50 border border-zinc-800/70 rounded-md py-2 text-center"
              >
                <div className="text-[20px] leading-none font-bold text-zinc-50 font-heading">
                  {String(part.v).padStart(2, "0")}
                </div>
                <div className="text-[9.5px] text-zinc-500 mt-1 uppercase tracking-[0.15em] font-mono">
                  {part.l}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NextDeparture;
